import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Eye, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Client, CreateClientForm, UpdateClientForm } from '../types';
import DataGrid from '../components/grid/DataGrid';
import FormModal from '../components/modals/FormModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import ClientForm from '../components/forms/ClientForm';
import ClientCreationWizard from '../components/clients/ClientCreationWizard';
import ClientLocationsPanel from '../components/clients/ClientLocationsPanel';
import { formatPhoneNumber } from '../utils/formatters';

const ClientsPage: React.FC = () => {
	
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [sortField, setSortField] = useState<string>('name');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedClient, setSelectedClient] = useState<Client | null>(null);
	const [useWizard, setUseWizard] = useState(true);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	
	
	// Check if this is a system admin session
	const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
	
	// Check authentication
	const token = localStorage.getItem('token');
	const user = localStorage.getItem('user');
	console.log('Authentication check:', {
		hasToken: !!token,
		tokenLength: token?.length,
		hasUser: !!user,
		userData: user ? JSON.parse(user) : null
	});

	// Decode JWT token to check claims
	try {
		const tokenParts = token?.split('.');
		if (tokenParts && tokenParts.length === 3) {
			const payload = JSON.parse(atob(tokenParts[1]));
			console.log('JWT Token payload:', payload);
			console.log('CompanyId from token:', payload.CompanyId);
		}
	} catch (error) {
		console.error('Error decoding JWT token:', error);
	}

	// Fetch clients data
	const {
		data: clientsResponse,
		isLoading,
		error,
		refetch
	} = useQuery({
		queryKey: ['clients', currentPage, pageSize, searchTerm, sortField, sortDirection],
		queryFn: () => apiService.getClients(currentPage, pageSize, searchTerm, sortField, sortDirection),
		enabled: !!token && !isSystemAdmin
	});

	// Create client mutation
	const createClientMutation = useMutation({
		mutationFn: async (data: CreateClientForm) => {
			console.log('Calling API with data:', data);
			const result = await apiService.createClient(data);
			console.log('API response:', result);
			return result;
		},
		onSuccess: (response) => {
			console.log('Mutation success:', response);
			if (response.success && response.data) {
				// Invalidate all client queries to refresh the data
				queryClient.invalidateQueries({ queryKey: ['clients'] });
				setIsAddModalOpen(false);
				// Show success message
				toast.success(`Client "${response.data.name}" created successfully!`);
			} else {
				alert(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error creating client:', error);
			const errorMessage = error?.response?.data?.message || 
								error?.response?.data?.errors?.join(', ') || 
								error.message || 
								'Unknown error';
			alert(`Error creating client: ${errorMessage}`);
		}
	});

	// Create client with location mutation
	const createClientWithLocationMutation = useMutation({
		mutationFn: async ({ clientData, locationData }: { clientData: CreateClientForm; locationData: any }) => {
			// First create the client
			const clientResponse = await apiService.createClient(clientData);
			if (!clientResponse.success || !clientResponse.data) {
				throw new Error('Failed to create client');
			}
			
			// Then create the location
			const locationResponse = await apiService.createClientLocation(clientResponse.data.id, locationData);
			if (!locationResponse.success) {
				throw new Error('Failed to create location');
			}
			
			return clientResponse.data;
		},
		onSuccess: (response) => {
			queryClient.invalidateQueries({ queryKey: ['clients'] });
			setIsAddModalOpen(false);
			toast.success(`Client "${response.name}" and primary location created successfully!`);
		},
		onError: (error: any) => {
			console.error('Error creating client with location:', error);
			const errorMessage = error?.response?.data?.message || 
								error?.response?.data?.errors?.join(', ') || 
								error.message || 
								'Unknown error';
			alert(`Error creating client: ${errorMessage}`);
		}
	});

	// Update client mutation
	const updateClientMutation = useMutation({
		mutationFn: ({ id, data }: { id: number; data: UpdateClientForm }) => {
			console.log('ClientsPage: updateClientMutation.mutationFn called with:', { id, data });
			return apiService.updateClient(id, data);
		},
		onSuccess: (response) => {
			console.log('ClientsPage: updateClientMutation.onSuccess called with:', response);
			if (response.success && response.data) {
				// Invalidate all client queries to refresh the data
				queryClient.invalidateQueries({ queryKey: ['clients'] });
				setIsEditModalOpen(false);
				setSelectedClient(null);
				// Show success message
				toast.success(`Client "${response.data.name}" updated successfully!`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('ClientsPage: updateClientMutation.onError called with:', error);
			const errorMessage = error?.response?.data?.message || 
								error?.response?.data?.errors?.join(', ') || 
								error.message || 
								'Unknown error';
			toast.error(`Error updating client: ${errorMessage}`);
		}
	});

	// Delete client mutation
	const deleteClientMutation = useMutation({
		mutationFn: (id: number) => apiService.deleteClient(id),
		onSuccess: (response) => {
			if (response.success) {
				// Invalidate all client queries to refresh the data
				queryClient.invalidateQueries({ queryKey: ['clients'] });
				setIsDeleteModalOpen(false);
				setSelectedClient(null);
				// Show success message
				toast.success(`Client "${selectedClient?.name}" deleted successfully!`);
			} else {
				alert(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error deleting client:', error);
			const errorMessage = error?.response?.data?.message || 
								error?.response?.data?.errors?.join(', ') || 
								error.message || 
								'Unknown error';
			alert(`Error deleting client: ${errorMessage}`);
		}
	});

	const handleAddClient = (data: CreateClientForm) => {
		console.log('Creating client with data:', data);
		createClientMutation.mutate(data);
	};

	const handleAddClientWithLocation = (clientData: CreateClientForm, locationData: any) => {
		createClientWithLocationMutation.mutate({ clientData, locationData });
	};

	const handleEditClient = (data: UpdateClientForm) => {
		console.log('ClientsPage: handleEditClient called with data:', data);
		console.log('ClientsPage: selectedClient:', selectedClient);
		if (selectedClient) {
			console.log('ClientsPage: Calling updateClientMutation.mutate');
			updateClientMutation.mutate({ id: selectedClient.id, data });
		} else {
			console.log('ClientsPage: No selectedClient, cannot update');
		}
	};

	const handleDeleteClient = () => {
		if (selectedClient) {
			deleteClientMutation.mutate(selectedClient.id);
		}
	};

	const openEditModal = (client: Client) => {
		setSelectedClient(client);
		setIsEditModalOpen(true);
	};

	const openDeleteModal = (client: Client) => {
		setSelectedClient(client);
		setIsDeleteModalOpen(true);
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setCurrentPage(1); // Reset to first page when sorting
	};

	const columns = useMemo(() => [
		{
			id: 'name',
			header: 'Name',
			sortable: true,
			cell: (value: any, row: Client) => (
				<div className="font-medium text-gray-900 dark:text-gray-100">{row.name}</div>
			)
		},
		{
			id: 'email',
			header: 'Email',
			sortable: true,
			cell: (value: any, row: Client) => (
				<div className="text-gray-600 dark:text-gray-300">{row.email || '-'}</div>
			)
		},
		{
			id: 'phone',
			header: 'Phone',
			sortable: true,
			cell: (value: any, row: Client) => (
				<div className="text-gray-600 dark:text-gray-300">{formatPhoneNumber(row.phone)}</div>
			)
		},
		{
			id: 'location',
			header: 'Primary Location',
			sortable: true,
			cell: (value: any, row: Client) => (
				<div className="text-gray-600 dark:text-gray-300">
					{row.primaryLocationName ? (
						<div>
							<div className="font-medium text-gray-900 dark:text-gray-100">{row.primaryLocationName}</div>
							{row.primaryLocationAddress && (
								<div className="text-xs text-gray-500 dark:text-gray-400">{row.primaryLocationAddress}</div>
							)}
							{row.primaryLocationCity && row.primaryLocationState && (
								<div className="text-xs text-gray-500 dark:text-gray-400">{row.primaryLocationCity}, {row.primaryLocationState}</div>
							)}
							{row.locationCount > 1 && (
								<div className="text-xs text-accent-color dark:text-accent-color-400 mt-1">
									+{row.locationCount - 1} more location{row.locationCount - 1 !== 1 ? 's' : ''}
								</div>
							)}
						</div>
					) : (
						<div className="text-gray-400">No locations</div>
					)}
				</div>
			)
		},
		{
			id: 'rank',
			header: 'Rank',
			sortable: true,
			cell: (value: any, row: Client) => (
				<div className="text-gray-600 dark:text-gray-300">{row.rank || '-'}</div>
			)
		},
		{
			id: 'isActive',
			header: 'Status',
			sortable: true,
			cell: (value: boolean, row: Client) => (
				<div className="relative">
					<div className="group-hover:opacity-0 transition-opacity duration-200">
						<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
							value 
								? 'bg-green-100 text-green-800' 
								: 'bg-red-100 text-red-800'
						}`}>
							{value ? 'Active' : 'Inactive'}
						</span>
					</div>
					<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
						<button 
							onClick={() => navigate(`/clients/${row.id}`)}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="View Details"
						>
							<Eye className="h-4 w-4"/>
						</button>
						<button 
							onClick={() => navigate(`/clients/${row.id}/edit`)}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="Edit Client"
						>
							<Edit className="h-4 w-4"/>
						</button>
						<button 
							onClick={() => openDeleteModal(row)}
							className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
							title="Delete Client"
						>
							<Trash2 className="h-4 w-4"/>
						</button>
					</div>
				</div>
			)
		}
	], []);

	// Check authentication after all hooks are declared
	if (!token || !user) {
		console.log('Not authenticated, redirecting to login');
		window.location.href = '/login';
		return null;
	}

	if (error) {
		return (
			<div className="space-y-6">
				<div className="flex items-center space-x-2">
					<Building2 className="h-6 w-6 text-gray-900 dark:text-gray-100" />
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Clients</h1>
				</div>
				<div className="bg-white shadow rounded-lg p-6">
					<div className="text-center text-red-600">
						Error loading clients. Please try again.
					</div>
					<button
						onClick={() => refetch()}
						className="mt-4 btn-primary"
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	// Check if this is a system admin session and return early
	if (isSystemAdmin) {
		return (
			<div className="space-y-6">
				<div className="flex justify-between items-center">
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Clients</h1>
				</div>
				<div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-6">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<svg className="h-5 w-5 text-accent-color-400" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-sm font-medium text-accent-color-800 dark:text-accent-color-200">
								System Administrator Access
							</h3>
							<div className="mt-2 text-sm text-accent-color-700 dark:text-accent-color-300">
								<p>As a system administrator, you don't have access to company-specific clients. Use the System Admin panel to manage companies and their data.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<div className="flex items-center space-x-2">
						<Building2 className="h-6 w-6 text-gray-900 dark:text-gray-100" />
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Clients</h1>
					</div>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your clients and contacts</p>
				</div>
				<button
					onClick={() => setIsAddModalOpen(true)}
					className="btn-primary flex items-center space-x-2"
				>
					<Plus className="h-4 w-4" />
					<span>Add Client</span>
				</button>
			</div>

			{/* System Admin Message */}
			{isSystemAdmin && (
				<div className="bg-accent-color-50 border border-accent-color-200 rounded-md p-4 mb-6">
					<div className="flex">
						<div className="flex-shrink-0">
							<svg className="h-5 w-5 text-accent-color-400" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-sm font-medium text-accent-color-800">
								System Admin View
							</h3>
							<div className="mt-2 text-sm text-accent-color-700">
								<p>As a system administrator, you don't have access to company-specific clients. Use the Companies section to manage individual company data.</p>
							</div>
						</div>
					</div>
				</div>
			)}

			<DataGrid
				data={clientsResponse?.data?.data || []}
				columns={columns}
				isLoading={isLoading}
				searchTerm={searchTerm}
				onSearch={setSearchTerm}
				currentPage={currentPage}
				pageSize={pageSize}
				totalCount={clientsResponse?.data?.totalCount || 0}
				totalPages={clientsResponse?.data?.totalPages || 1}
				onPageChange={setCurrentPage}
				onPageSizeChange={() => {}} // Not used in this page
				onSort={handleSort}
				sortField={sortField}
				sortDirection={sortDirection}
				searchPlaceholder="Search by name, email, phone, or location..."
				emptyMessage="No clients found. Try adjusting your search criteria."
				onRowClick={(row) => navigate(`/clients/${(row as Client).id}`)}
				rowTooltip="View Client"
			/>

			{/* Add Client Modal */}
			<FormModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				title="Add New Client"
				size="4xl"
			>
				{useWizard ? (
					<ClientCreationWizard
						onSubmit={handleAddClientWithLocation}
						isLoading={createClientWithLocationMutation.isPending}
						error={createClientWithLocationMutation.error?.message}
						onCancel={() => setIsAddModalOpen(false)}
					/>
				) : (
					<ClientForm
						onSubmit={handleAddClient}
						isLoading={createClientMutation.isPending}
						error={createClientMutation.error?.message}
					/>
				)}
			</FormModal>

			{/* Edit Client Modal */}
			<FormModal
				isOpen={isEditModalOpen}
				onClose={() => {
					setIsEditModalOpen(false);
					setSelectedClient(null);
				}}
				title="Edit Client"
				size="2xl"
			>
				<div className="space-y-6">
				<ClientForm
					client={selectedClient}
					onSubmit={handleEditClient}
					isLoading={updateClientMutation.isLoading}
					error={updateClientMutation.error?.message}
				/>
				{selectedClient && (
					<div className="pt-4 border-t">
						<ClientLocationsPanel clientId={selectedClient.id} />
					</div>
				)}
				</div>
			</FormModal>

			{/* Delete Confirmation Modal */}
			<ConfirmDialog
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedClient(null);
				}}
				onConfirm={handleDeleteClient}
				title="Delete Client"
				message={`Are you sure you want to delete "${selectedClient?.name}"? This action cannot be undone.`}
				confirmText="Delete"
				variant="danger"
				isLoading={deleteClientMutation.isLoading}
			/>

		</div>
	);
};

export default ClientsPage;
